import CustomButton from "@/components/common/Button";
import CodeEditor from "@/components/common/CodeEditor";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { LetterHeadService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Card, Checkbox, Col, Form, Input, Row, Select } from "antd";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const AddLetterHeadPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const { id } = useParams();
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State for select values
  const [headerType, setHeaderType] = useState<string>("");
  const [footerType, setFooterType] = useState<string>("");
  // State for images
  const [headerImage, setHeaderImage] = useState<File | null>(null);
  const [footerImage, setFooterImage] = useState<File | null>(null);
  // State for image properties
  const [headerImageWidth, setHeaderImageWidth] = useState<number>(240);
  const [headerImageHeight, setHeaderImageHeight] = useState<number>(80);
  const [headerImageAlign, setHeaderImageAlign] = useState<string>("left");
  const [footerImageWidth, setFooterImageWidth] = useState<number>(240);
  const [footerImageHeight, setFooterImageHeight] = useState<number>(80);
  const [footerImageAlign, setFooterImageAlign] = useState<string>("left");
  // Use React Query for getting letter head by ID
  const { data: letterHead, isLoading } = useQuery({
    queryKey: ["letterHead", id],
    queryFn: () => LetterHeadService.getLetterHeadById(id ?? ""),
    enabled: !!id,
  });

  useEffect(() => {
    if (isEditMode && letterHead) {
      form.setFieldsValue({
        name: letterHead.name,
        header_type: letterHead.header_type,
        is_default: letterHead.is_default,
        header_html: letterHead.header_html ?? "",
        footer_type: letterHead.footer_type,
        footer_html: letterHead.footer_html ?? "",
      });
      setHeaderType(letterHead.header_type ?? "html");
      setFooterType(letterHead.footer_type ?? "html");
      setHeaderImageWidth(letterHead.header_image_width ?? 240);
      setHeaderImageHeight(letterHead.header_image_height ?? 80);
      setHeaderImageAlign(letterHead.header_image_align ?? "left");
      setFooterImageWidth(letterHead.footer_image_width ?? 240);
      setFooterImageHeight(letterHead.footer_image_height ?? 80);
      setFooterImageAlign(letterHead.footer_image_align ?? "left");
    }
  }, [isEditMode, letterHead, form]);

  if (isEditMode && isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  if (isEditMode && !letterHead) {
    return <NotFound />;
  }

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      // Validate that required fields are filled based on selected types
      if (headerType === "html" && !values.header_html?.trim()) {
        notify.error("Header HTML is required when header type is HTML");
        setIsSubmitting(false);
        return;
      }

      if (headerType === "image" && !headerImage) {
        notify.error("Header image is required when header type is Image");
        setIsSubmitting(false);
        return;
      }

      if (footerType === "html" && !values.footer_html?.trim()) {
        notify.error("Footer HTML is required when footer type is HTML");
        setIsSubmitting(false);
        return;
      }

      if (footerType === "image" && !footerImage) {
        notify.error("Footer image is required when footer type is Image");
        setIsSubmitting(false);
        return;
      }

      // Create payload with proper structure
      const payload: any = {
        name: values.name,
        header_type: headerType,
        footer_type: footerType,
        is_default: values.is_default ?? false,
      };

      // Header logic
      if (headerType === "image") {
        payload.header_image = headerImage;
        payload.header_image_width = headerImageWidth;
        payload.header_image_height = headerImageHeight;
        payload.header_image_align = headerImageAlign;
        // Don't include header_html when using image
      } else if (headerType === "html") {
        payload.header_html = values.header_html;
        // Don't include image fields when using HTML
      }

      // Footer logic
      if (footerType === "image") {
        payload.footer_image = footerImage;
        payload.footer_image_width = footerImageWidth;
        payload.footer_image_height = footerImageHeight;
        payload.footer_image_align = footerImageAlign;
        // Don't include footer_html when using image
      } else if (footerType === "html") {
        payload.footer_html = values.footer_html;
        // Don't include image fields when using HTML
      }

      if (isEditMode && letterHead) {
        // Check for changes before updating
        if (!checkFormChanges(payload, letterHead, notify)) {
          setIsSubmitting(false);
          return;
        }

        const response = await LetterHeadService.updateLetterHead(
          letterHead.id,
          payload
        );
        if (response) {
          navigate(navigationRoutes.LETTER_HEAD);
        }
      } else {
        const response = await LetterHeadService.createLetterHead(payload);
        if (response) {
          navigate(navigationRoutes.LETTER_HEAD);
        }
      }
    } catch (error) {
      console.error("Form validation error:", error);
      notify.error("Please fill in all required fields");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={isEditMode ? "Edit Letter Head" : "Add Letter Head"}
          onBack={() => navigate(navigationRoutes.LETTER_HEAD)}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
              title={
                isEditMode ? "Save Letter Head Changes" : "Save New Letter Head"
              }
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Letter Head Name"
              rules={[
                { required: true, message: "Please enter letter head name" },
              ]}
            >
              <Input placeholder="Enter letter head name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="is_default"
              valuePropName="checked"
              style={{ marginTop: 30 }}
            >
              <Checkbox>Default Letter Head</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="header_type"
              label="Letter Head Based On"
              rules={[
                { required: true, message: "Please select letter head type" },
              ]}
            >
              <Select
                placeholder="Select type"
                value={headerType}
                onChange={value => setHeaderType(value)}
                allowClear
              >
                <Select.Option value="html">HTML</Select.Option>
                <Select.Option value="image">Image</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="footer_type"
              label="Footer Based On"
              rules={[
                { required: true, message: "Please select Footer Based On" },
              ]}
            >
              <Select
                placeholder="Select Footer Based On"
                value={footerType}
                onChange={value => setFooterType(value)}
                allowClear
              >
                <Select.Option value="html">HTML</Select.Option>
                <Select.Option value="image">Image</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Header HTML or Image */}
        {headerType === "html" && (
          <Form.Item
            label="Header HTML Template"
            name="header_html"
            required={headerType === "html"}
            validateTrigger="onBlur"
            rules={[
              {
                required: headerType === "html",
                message: "Please enter HTML template",
              },
            ]}
          >
            <CodeEditor
              value={form.getFieldValue("header_html")}
              handleChange={(value: string) =>
                form.setFieldsValue({ header_html: value })
              }
              language="html"
            />
          </Form.Item>
        )}

        {headerType === "image" && (
          <>
            <Form.Item
              label="Header Image"
              required={headerType === "image"}
              rules={[
                {
                  required: headerType === "image",
                  message: "Please select header image",
                },
              ]}
            >
              <input
                type="file"
                accept="image/*"
                onChange={e => {
                  if (e.target.files?.[0]) {
                    setHeaderImage(e.target.files[0]);
                  }
                }}
              />
            </Form.Item>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Image Width (px)"
                  required={headerType === "image"}
                >
                  <Input
                    type="number"
                    min={1}
                    value={headerImageWidth}
                    onChange={e => setHeaderImageWidth(Number(e.target.value))}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Image Height (px)"
                  required={headerType === "image"}
                >
                  <Input
                    type="number"
                    min={1}
                    value={headerImageHeight}
                    onChange={e => setHeaderImageHeight(Number(e.target.value))}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Align" required={headerType === "image"}>
                  <Select
                    value={headerImageAlign}
                    onChange={setHeaderImageAlign}
                  >
                    <Select.Option value="left">Left</Select.Option>
                    <Select.Option value="center">Center</Select.Option>
                    <Select.Option value="right">Right</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {/* Footer HTML or Image */}
        {footerType === "html" && (
          <Form.Item
            label="Footer HTML Template"
            name="footer_html"
            required={footerType === "html"}
            validateTrigger="onBlur"
            rules={[
              {
                required: footerType === "html",
                message: "Please enter Footer HTML",
              },
            ]}
          >
            <CodeEditor
              value={form.getFieldValue("footer_html")}
              handleChange={(value: string) =>
                form.setFieldsValue({ footer_html: value })
              }
              language="html"
            />
          </Form.Item>
        )}

        {footerType === "image" && (
          <>
            <Form.Item
              label="Footer Image"
              required={footerType === "image"}
              rules={[
                {
                  required: footerType === "image",
                  message: "Please select footer image",
                },
              ]}
            >
              <input
                type="file"
                accept="image/*"
                onChange={e => {
                  if (e.target.files?.[0]) {
                    setFooterImage(e.target.files[0]);
                  }
                }}
              />
            </Form.Item>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Image Width (px)"
                  required={footerType === "image"}
                >
                  <Input
                    type="number"
                    min={1}
                    value={footerImageWidth}
                    onChange={e => setFooterImageWidth(Number(e.target.value))}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Image Height (px)"
                  required={footerType === "image"}
                >
                  <Input
                    type="number"
                    min={1}
                    value={footerImageHeight}
                    onChange={e => setFooterImageHeight(Number(e.target.value))}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Align" required={footerType === "image"}>
                  <Select
                    value={footerImageAlign}
                    onChange={setFooterImageAlign}
                  >
                    <Select.Option value="left">Left</Select.Option>
                    <Select.Option value="center">Center</Select.Option>
                    <Select.Option value="right">Right</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </Form>
    </Card>
  );
};

export default AddLetterHeadPage;
